import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import { Sparkles, Search, ChevronRight } from 'lucide-react';
import { categoriesAPI } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { useSelectedCity } from '@/lib/selectedCity';
import { buildGroups, pickPopular, GROUP_BY_SLUG, normalizeServerTree } from '@/lib/categoryGroups';

function toPascal(str) {
  if (!str) return '';
  return String(str)
    .split(/[-_ ]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('');
}

/** Big picture-card tile used in the 2-column super-category grid. */
function GroupTile({ group, onClick, language, showCount }) {
  const iconKey = toPascal(group.icon);
  const IconCmp = (iconKey && LucideIcons[iconKey]) || Sparkles;
  const label = language === 'es' ? group.name_es : group.name_en;
  const subLabel =
    language === 'es'
      ? `${group.leafItems.length} tipo${group.leafItems.length === 1 ? '' : 's'}`
      : `${group.leafItems.length} type${group.leafItems.length === 1 ? '' : 's'}`;
  const bizCount = group.business_count || 0;
  const img = group.image_url;

  return (
    <motion.button
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      className="group relative overflow-hidden rounded-2xl bg-white dark:bg-[#1E1A17] border border-[#EAE3D9] dark:border-[#332D29] shadow-[0_4px_16px_rgba(42,42,42,0.05)] text-left"
      data-testid={`group-tile-${group.slug}`}
    >
      <div className="aspect-square relative bg-gradient-to-br from-[#F3EFE9] to-[#EAE3D9] dark:from-[#2A2522] dark:to-[#332D29]">
        {img ? (
          <img
            src={img}
            alt={label}
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
        <div className="absolute top-2 left-2 h-8 w-8 rounded-lg bg-white/90 dark:bg-black/60 backdrop-blur grid place-items-center">
          <IconCmp className="h-4 w-4 text-[#E85A4F]" strokeWidth={2.2} />
        </div>
        {showCount && bizCount > 0 && (
          <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur text-white text-[10px] font-semibold">
            {bizCount}
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <p className="font-display font-bold text-white text-[15px] leading-tight drop-shadow-md">
            {label}
          </p>
          <p className="mt-0.5 text-[11px] font-medium text-white/85 drop-shadow-md">
            {subLabel}
          </p>
        </div>
      </div>
    </motion.button>
  );
}

/** Small horizontally-scrollable chip used in "Populares". */
function PopularChip({ category, onClick, language }) {
  const iconKey = toPascal(category.icon);
  const IconCmp = (iconKey && LucideIcons[iconKey]) || Sparkles;
  const label = language === 'es'
    ? (category.name_es || category.name)
    : (category.name_en || category.name_es || category.name);

  return (
    <motion.button
      whileTap={{ scale: 0.94 }}
      onClick={onClick}
      className="shrink-0 flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full bg-white dark:bg-[#1E1A17] border border-[#EAE3D9] dark:border-[#332D29] shadow-[0_2px_8px_rgba(42,42,42,0.04)] active:shadow-none"
      data-testid={`popular-chip-${category.slug}`}
    >
      <span className="h-7 w-7 rounded-full bg-[#FEF3F1] dark:bg-[#332D29] grid place-items-center">
        <IconCmp className="h-3.5 w-3.5 text-[#E85A4F]" strokeWidth={2.4} />
      </span>
      <span className="font-body font-semibold text-[13px] text-[#2A2A2A] dark:text-[#F5F2ED] whitespace-nowrap">
        {label}
      </span>
    </motion.button>
  );
}

/** Row shown when the user is searching — matches a leaf or group. */
function SearchResultRow({ item, onClick, language }) {
  const iconKey = toPascal(item.icon);
  const IconCmp = (iconKey && LucideIcons[iconKey]) || Sparkles;
  const label = language === 'es'
    ? (item.name_es || item.name)
    : (item.name_en || item.name_es || item.name);
  const parentGroupSlug = item._isGroup ? null : (item._parentGroup || null);
  const parentGroupName = parentGroupSlug
    ? (language === 'es'
        ? GROUP_BY_SLUG[parentGroupSlug]?.name_es
        : GROUP_BY_SLUG[parentGroupSlug]?.name_en)
    : null;

  return (
    <motion.button
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-white dark:bg-[#1E1A17] border border-[#EAE3D9] dark:border-[#332D29] text-left"
      data-testid={`search-result-${item.slug}`}
    >
      <span className="h-10 w-10 rounded-xl bg-[#FEF3F1] dark:bg-[#332D29] grid place-items-center shrink-0">
        <IconCmp className="h-5 w-5 text-[#E85A4F]" strokeWidth={2.2} />
      </span>
      <span className="flex-1 min-w-0">
        <p className="font-body font-semibold text-[15px] text-[#2A2A2A] dark:text-[#F5F2ED] truncate">
          {label}
        </p>
        {parentGroupName && (
          <p className="text-[12px] text-[#737373] dark:text-[#A39F99] truncate">
            {parentGroupName}
          </p>
        )}
        {item._isGroup && (
          <p className="text-[12px] text-[#737373] dark:text-[#A39F99] truncate">
            {language === 'es' ? 'Categoría general' : 'General category'}
          </p>
        )}
      </span>
      <ChevronRight className="h-4 w-4 text-[#A39F99] shrink-0" />
    </motion.button>
  );
}

export default function CategoriesPage() {
  const { language } = useI18n();
  const navigate = useNavigate();
  const [city] = useSelectedCity();
  const [categories, setCategories] = useState([]);
  const [serverTree, setServerTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let mounted = true;
    const params = city ? { city, keep_empty: true } : {};

    // Preferred source: the pre-computed 2-level tree from the API. If it
    // is unavailable (older backend, network hiccup), fall back to the
    // flat catalog and reconstruct the tree locally with the same mapping.
    Promise.all([
      categoriesAPI.getGroupedTree(params).catch(() => null),
      categoriesAPI.getAll(params).catch(() => null),
    ])
      .then(([treeRes, flatRes]) => {
        if (!mounted) return;
        const tree = treeRes?.data && treeRes.data.groups
          ? treeRes.data
          : null;
        const flat = Array.isArray(flatRes?.data)
          ? flatRes.data
          : flatRes?.data?.items || flatRes?.data?.data || [];
        setServerTree(tree);
        setCategories(flat);
      })
      .finally(() => setLoading(false));
    return () => { mounted = false; };
  }, [city]);

  const groups = useMemo(() => {
    if (serverTree) return normalizeServerTree(serverTree).groups;
    return buildGroups(categories);
  }, [serverTree, categories]);

  const popular = useMemo(() => {
    if (serverTree?.populares?.length) return serverTree.populares;
    return pickPopular(categories, 6);
  }, [serverTree, categories]);

  // Decorate each leaf category with its parent super-category slug so search
  // results can display a helpful breadcrumb ("Barbería  ·  Belleza").
  const leavesWithParent = useMemo(() => {
    return (categories || []).map((c) => {
      const parent = groups.find((g) => g.leaves.includes(c.slug))?.slug || null;
      return { ...c, _parentGroup: parent };
    });
  }, [categories, groups]);

  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const matchName = (c) => {
      const es = (c.name_es || '').toLowerCase();
      const en = (c.name_en || '').toLowerCase();
      const slug = (c.slug || '').toLowerCase();
      return es.includes(q) || en.includes(q) || slug.includes(q);
    };
    // Groups first (broader match), then leaves.
    const groupHits = groups
      .filter(matchName)
      .map((g) => ({ ...g, _isGroup: true }));
    const leafHits = leavesWithParent
      .filter((c) => matchName(c) && !groupHits.some((g) => g.slug === c.slug));
    return [...groupHits, ...leafHits];
  }, [query, groups, leavesWithParent]);

  const goToGroup = (group) => {
    // Skip the sub-category screen when the group only maps to one leaf —
    // there is nothing to pick, just jump straight to the businesses list.
    if (group.leafItems.length === 1) {
      const leaf = group.leafItems[0];
      const target = city
        ? `/search?category=${leaf.id || leaf.slug}&city=${encodeURIComponent(city)}`
        : `/search?category=${leaf.id || leaf.slug}`;
      navigate(target);
      return;
    }
    navigate(`/categories/${group.slug}`);
  };

  const goToLeaf = (leaf) => {
    const target = city
      ? `/search?category=${leaf.id || leaf.slug}&city=${encodeURIComponent(city)}`
      : `/search?category=${leaf.id || leaf.slug}`;
    navigate(target);
  };

  return (
    <div
      className="min-h-screen bg-[#FAF7F2] dark:bg-[#12100E] pt-0 md:pt-20 pb-24 font-body"
      data-testid="categories-page"
    >
      <div className="px-5">
        <h1 className="font-display font-bold text-[30px] leading-tight text-[#2A2A2A] dark:text-[#F5F2ED]">
          {language === 'es' ? 'Categorías' : 'Categories'}
        </h1>
        <p className="mt-1 text-[14px] text-[#737373] dark:text-[#A39F99]">
          {language === 'es'
            ? 'Encuentra lo que necesitas en segundos'
            : 'Find what you need in seconds'}
        </p>

        <div className="mt-5 flex items-center gap-3 px-4 py-3 rounded-2xl bg-white dark:bg-[#1E1A17] border border-[#EAE3D9] dark:border-[#332D29] shadow-[0_2px_8px_rgba(42,42,42,0.03)]">
          <Search className="h-5 w-5 text-[#E85A4F] shrink-0" strokeWidth={2.2} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={language === 'es' ? 'Buscar servicio o categoría…' : 'Search service or category…'}
            className="flex-1 bg-transparent border-0 focus:outline-none text-[15px] font-medium text-[#2A2A2A] dark:text-[#F5F2ED] placeholder:text-[#A3A3A3]"
            data-testid="categories-search-input"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="text-[12px] font-semibold text-[#E85A4F]"
              data-testid="clear-search-btn"
            >
              {language === 'es' ? 'Limpiar' : 'Clear'}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="px-5 mt-6">
          <div className="grid grid-cols-2 gap-3" data-testid="categories-loading">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-square rounded-2xl bg-white/50 dark:bg-white/5 animate-pulse" />
            ))}
          </div>
        </div>
      ) : query ? (
        // Search-first view: replace the whole page with results while typing.
        <div className="px-5 mt-6 space-y-3" data-testid="search-results-list">
          {searchResults.length ? (
            searchResults.map((item) => (
              <SearchResultRow
                key={(item._isGroup ? 'g:' : 'c:') + item.slug}
                item={item}
                language={language}
                onClick={() => (item._isGroup ? goToGroup(item) : goToLeaf(item))}
              />
            ))
          ) : (
            <div className="text-center py-16" data-testid="search-empty">
              <p className="text-[#737373] dark:text-[#A39F99] text-[15px]">
                {language === 'es' ? 'Sin resultados para tu búsqueda' : 'No results for your search'}
              </p>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Populares */}
          {popular.length > 0 && (
            <div className="mt-7" data-testid="populares-section">
              <div className="px-5 flex items-center gap-2 mb-3">
                <Sparkles className="h-4 w-4 text-[#E85A4F]" strokeWidth={2.4} />
                <h2 className="font-display font-bold text-[17px] text-[#2A2A2A] dark:text-[#F5F2ED]">
                  {language === 'es' ? 'Populares' : 'Popular'}
                </h2>
              </div>
              <div className="pl-5 pr-3 pb-2 flex gap-2 overflow-x-auto no-scrollbar">
                {popular.map((c) => (
                  <PopularChip
                    key={c.slug}
                    category={c}
                    language={language}
                    onClick={() => goToLeaf(c)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* 10 super-categories grid */}
          <div className="px-5 mt-6">
            <h2 className="font-display font-bold text-[17px] text-[#2A2A2A] dark:text-[#F5F2ED] mb-3">
              {language === 'es' ? 'Explora por categoría' : 'Browse by category'}
            </h2>
            <div className="grid grid-cols-2 gap-3" data-testid="groups-grid">
              {groups.map((g) => (
                <GroupTile
                  key={g.slug}
                  group={g}
                  language={language}
                  showCount={!!city}
                  onClick={() => goToGroup(g)}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
